import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatBadgeModule } from '@angular/material/badge';
import { DialogParams } from '@interfaces/dialogParam.interface.model';
import { BaseDto } from '@models/base/baseDto.model';
import { AccionesDialog } from '@models/base/identificadores.model';
import { CasoDto } from '@models/caso/casoDto.model';
import { CasoService } from '@services/caso/caso.service';
import { SnackBarService } from '@services/snackBar/snack-bar-service.service';
import { DialogFormCasoComponent } from '@views/casos/dialog-form-caso/dialog-form-caso.component';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { Observable, map, startWith, debounceTime, distinctUntilChanged, filter } from 'rxjs';
import { isFalsy } from 'utility-types';

@Component({
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatBadgeModule,
    ReactiveFormsModule,
    NgxSpinnerModule,
    CommonModule
  ],
  selector: 'app-buscar-caso',
  templateUrl: './buscar-caso.component.html',
  styleUrls: ['./buscar-caso.component.css']
})
export class BuscarCasoComponent {
  @Input() disable: boolean = false;
  @Input() mostrarBotonNuevo: boolean = false;
  @Input() idCasoInput: number = 0;
  @Input() casoEntrada: BaseDto = new BaseDto();
  @Output() eventoCaso = new EventEmitter<BaseDto>();
  @Output() eventEntity = new EventEmitter<BaseDto>();
  @ViewChild('typehead', { read: MatAutocompleteTrigger }) autoTrigger!: MatAutocompleteTrigger;

  entitySelect!: number | null;
  casosDto!: BaseDto[];
  expedienteDto!: BaseDto;
  casoDto!: CasoDto;
  accionesDialog = AccionesDialog;

  // Configuración de búsqueda
  readonly MIN_CARACTERES = 3;
  readonly MAX_RESULTADOS = 50;
  readonly DEBOUNCE_TIME = 300;

  public entityControl = new FormControl('', [
    Validators.minLength(this.MIN_CARACTERES)
  ]);
  public filteredCaso!: Observable<BaseDto[]>;
  public resultadosCount = 0;
  public mostrarSinResultados = false;

  constructor(
    private dialog: MatDialog,
    private casoService: CasoService,
    private snackBar: SnackBarService,
    private spinner: NgxSpinnerService,
  ) {}

  ngOnInit(): void {
    this.getAll();
    this.setupFiltros();
    this.inicializarValorEntrada();
  }

  ngOnChanges(): void {
    if (this.casoEntrada?.descripcion) {
      this.entityControl.setValue(this.casoEntrada.descripcion);
      this.entitySelect = this.casoEntrada.id;
    }
  }

  private setupFiltros(): void {
    this.filteredCaso = this.entityControl.valueChanges.pipe(
      startWith(''),
      debounceTime(this.DEBOUNCE_TIME),
      distinctUntilChanged(),
      map((term: string | null) => this.filtrarCasos(term || ''))
    );
  }

  private inicializarValorEntrada(): void {
    if (this.casoEntrada?.descripcion) {
      this.entityControl.setValue(this.casoEntrada.descripcion);
      this.entitySelect = this.casoEntrada.id;
    }
  }

  private filtrarCasos(termino: string): BaseDto[] {
    if (!this.casosDto) {
      this.resultadosCount = 0;
      this.mostrarSinResultados = false;
      return [];
    }

    // Si el término es muy corto, no filtrar
    if (termino.length < this.MIN_CARACTERES) {
      this.resultadosCount = 0;
      this.mostrarSinResultados = false;
      return [];
    }

    const terminoLimpio = this.limpiarTexto(termino);

    const resultados = this.casosDto.filter(caso => {
      const descripcionLimpia = this.limpiarTexto(caso.descripcion);
      const idTexto = caso.id?.toString() || '';

      return descripcionLimpia.includes(terminoLimpio) ||
             idTexto.includes(terminoLimpio);
    }).slice(0, this.MAX_RESULTADOS);

    this.resultadosCount = resultados.length;
    this.mostrarSinResultados = termino.length >= this.MIN_CARACTERES && resultados.length === 0;

    return resultados;
  }

  private limpiarTexto(texto: string): string {
    return texto.toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, ''); // Eliminar acentos
  }

  selectValue(event: BaseDto | null): void {
    if (isFalsy(event)) {
      this.entitySelect = null;
      this.entityControl.setValue('');
      this.resultadosCount = 0;
      this.mostrarSinResultados = false;
      this.eventEntity.emit(undefined);
    } else {
      this.entitySelect = event!.id;
      this.entityControl.setValue(event!.descripcion);
      // Cerrar autocomplete automáticamente
      setTimeout(() => this.autoTrigger.closePanel(), 100);
      this.eventEntity.emit(event);
    }
  }

  getAll(): void {
    this.spinner.show("spBusqueda");
    this.casoService.getAllCaratula().subscribe({
      next: (data) => {
        this.spinner.hide("spBusqueda");
        this.casosDto = data;
        this.snackBar.showSuccess(`${data.length} casos cargados`, "Éxito");
      },
      error: (error) => {
        this.snackBar.showError(error, "Error");
        this.spinner.hide("spBusqueda");
      }
    });
  }

  actualizarCasos(): void {
    this.getAll();
  }

  abrirDialogoNuevo(event: Event): void {
    event.stopPropagation();
    this.autoTrigger.closePanel();
    this.openDialog({ accion: this.accionesDialog.Agregar, entidad: {}, modulo: 'caso' });
  }

  limpiarSeleccion(event: Event): void {
    event.stopPropagation();
    this.selectValue(null);
  }

  openDialog<T>(params: DialogParams<T>): void {
    const { accion, entidad } = params;

    const data = {
      ...entidad,
      accion,
    };

    const dialogConfig = new MatDialogConfig();
    dialogConfig.autoFocus = false;
    dialogConfig.width = "50%";
    dialogConfig.data = data;

    const dialogRef = this.dialog.open(DialogFormCasoComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(result => {
      if (!result || !result.event) return;

      switch (result.event) {
        case AccionesDialog.Agregar:
          this.actualizarCasos();
          break;
        case AccionesDialog.Modificar:
          this.actualizarCasos();
          break;
        case AccionesDialog.Eliminar:
          this.actualizarCasos();
          break;
        default:
          break;
      }
    });
  }

  trackByItems(index: number, item: BaseDto): number {
    return item.id || index;
  }

  // Getters para el template
  get hayResultados(): boolean {
    return this.resultadosCount > 0;
  }

  get textoPlaceholder(): string {
    return `Buscar por carátula, expediente o ID (mín. ${this.MIN_CARACTERES} caracteres)`;
  }

  get mensajeSinResultados(): string {
    return `No se encontraron casos que coincidan con la búsqueda`;
  }

  get textoContador(): string {
    const maxText = this.resultadosCount >= this.MAX_RESULTADOS ? '+ ' : '';
    return `${maxText}${this.resultadosCount} resultado${this.resultadosCount !== 1 ? 's' : ''}`;
  }
}
